/**
 * Política de clipe EXTERNO para avatares MetaPerson (catálogo + gerados).
 *
 * As três guardas de animação do AvatarCore (:1162 switch legado, :1246
 * preload do Group B, :1312 crossfade por mirrorState — números conferidos em
 * 10/08; os antigos :1073/:1146/:1206 ficaram para trás em 6f204f8 e chegaram
 * a ser citados como se fossem os atuais) bloqueiam FBX externo em modelo de
 * catálogo/gerado. O motivo real está em AvatarCore.tsx:906-910:
 * a biblioteca atual é Mixamo-rigged, e retarget Mixamo corrompe o esqueleto
 * MetaPerson. A guarda protege contra uma CLASSE de asset, não contra clipe
 * externo em si.
 *
 * Allowlist (autorização Rafa 09/08): clipes retargetados para metaperson_2.0
 * são servidos sob o segmento /avatar-animations/mp/ — e são a classe que a
 * guarda não precisa bloquear. A convenção é por PATH de propósito: o clipe
 * declara o rig no lugar onde vive, sem coluna nova nem config.
 *
 * Zero imports de propósito (padrão datetime-natural/waSoftAsk): o predicado
 * fica testável pelo vitest sem montar three.js — e fora do arquivo congelado,
 * o diff no AvatarCore se resume a importar e aplicar.
 */

/** O segmento que declara "este clipe foi retargetado para metaperson_2.0". */
const MP_RIGGED_SEGMENT = /\/avatar-animations\/mp\//i;

/** URL de clipe retargetado para o esqueleto MetaPerson? */
export function isMpRiggedClipUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return MP_RIGGED_SEGMENT.test(url);
}

/**
 * Extensão .glb (com ou sem query string)? Decide o LOADER do clipe externo
 * no AvatarCore (2ª mudança autorizada, 09/08): .glb → GLTF pelo cache do
 * avatarPreloader (o mesmo caminho dos modelos); qualquer outra coisa → FBX,
 * o caminho de sempre. Vive aqui (e não como regex local do AvatarCore) pela
 * mesma razão dos predicados acima: uma definição, testável sem three.js.
 */
const GLB_CLIP_SEGMENT = /\.glb(\?|$)/i;

export function isGlbClipUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return GLB_CLIP_SEGMENT.test(url);
}

/**
 * Conjunto do Group B (stateAnimations): TODAS as URLs precisam ser MP-rigged.
 *
 * Regra "todas", não "qualquer": o state machine pré-carrega os 5 clipes e
 * crossfada entre eles NO MESMO esqueleto. Um único Mixamo no meio do conjunto
 * reintroduziria a corrupção exatamente como a guarda existe para impedir — e
 * pior que o bloqueio total: intermitente, só em alguns estados, no meio de um
 * crossfade. Ou o conjunto inteiro é da classe permitida, ou nada passa.
 *
 * Conjunto vazio/ausente → false: não há nada a permitir, a guarda continua
 * fechada (um `every` vácuo devolveria true e destravaria com zero clipes).
 */
export function areAllMpRiggedClips(
  urls: Record<string, string> | null | undefined,
): boolean {
  if (!urls) return false;
  const values = Object.values(urls);
  if (values.length === 0) return false;
  return values.every(isMpRiggedClipUrl);
}

/**
 * Existe clipe EXTERNO da allowlist /mp/ dirigindo o rig NESTE frame?
 *
 * 3ª mudança escopada autorizada (Rafa, 10/08 — portal_decisions
 * 312d1622-bf41-4a8f-827a-9bb5283a9924). Consumido por um único ponto:
 * o tilt do Spine no animate() do AvatarCore, que escreve
 * `spine.rotation.x = modelTiltXRadians` de forma ABSOLUTA e DEPOIS do
 * `mixer.update()`. Enquanto nada anima a coluna essa escrita é a única
 * fonte de pose do Spine (o strip V3 tira Spine/Neck/Head do clipe
 * EMBUTIDO) e precisa continuar rodando byte a byte; com um clipe /mp/
 * externo — que anima o corpo inteiro — ela apagaria o eixo X da coluna
 * todo frame. Este predicado é o "está dirigindo?" que separa os dois.
 *
 * Mesma convenção das três guardas: quem declara o rig é o PATH do clipe.
 * Nenhum sinal novo, nenhuma coluna, nenhuma config.
 *
 * Duas pernas porque são dois caminhos distintos de troca no AvatarCore:
 *
 * 1. `currentClipUrl` — o switch de clipe único (Group A). É o valor de
 *    `currentAnimationUrlRef`, escrito só DEPOIS de a action tocar, então
 *    a perna tem o timing certo de graça. Vale `'embedded:<nome>'` para o
 *    clipe do catálogo e a URL FBX no bootstrap Avaturn: ambos NEGAM.
 *
 * 2. `stateClipsAllMpRigged && stateClipsLoaded` — o Group B. O state
 *    machine NÃO escreve em `currentAnimationUrlRef` (só em
 *    `currentActionRef`), então a perna 1 é cega para ele. O par
 *    "conjunto todo é /mp/" + "as actions já existem" é o equivalente
 *    exato: o predicado de conjunto é o MESMO que destrava as guardas
 *    :1246/:1312, e `stateClipsLoaded` é o `stateAnimationsLoadedRef`.
 *    Group B com os 5 Mixamo de hoje (o único conjunto que existe) dá
 *    `stateClipsAllMpRigged = false` → NEGA, e o tilt segue defendendo a
 *    coluna contra o retarget Mixamo, que é a razão de a linha existir.
 *
 * Group B está desligado nos dois consumidores desde 27/04. A perna 2 não
 * o religa — ela só evita que, no dia em que religar com clipe /mp/, o
 * mesmo clobber volte por uma porta que a perna 1 não enxerga.
 */
export function isMpRiggedClipDriving(args: {
  currentClipUrl: string | null | undefined;
  stateClipsAllMpRigged: boolean;
  stateClipsLoaded: boolean;
}): boolean {
  if (isMpRiggedClipUrl(args.currentClipUrl)) return true;
  return args.stateClipsAllMpRigged && args.stateClipsLoaded;
}
